import { Serializable } from "@redotech/json/serialize";
import { z } from "zod/v4";

export const unaryRpc = <
  Name extends string,
  Input extends Serializable,
  Output extends Serializable,
>(schemas: {
  name: Name;
  input: z.ZodType<Input>;
  output: z.ZodType<Output>;
}): UnaryRpcSchema<Name, Input, Output> => ({
  ...schemas,
  method: "unary" as const,
});

export const unaryStreamRpc = <
  Name extends string,
  Input extends Serializable,
  Output extends Serializable,
>(schemas: {
  name: Name;
  input: z.ZodType<Input>;
  output: z.ZodType<Output>;
}): UnaryStreamRpcSchema<Name, Input, Output> => ({
  ...schemas,
  method: "unary-stream" as const,
});

export type UnaryRpcSchema<Name extends string, Input, Output> = {
  name: Name;
  input: z.ZodType<Input>;
  output: z.ZodType<Output>;
  method?: "unary" | undefined;
};

export type UnaryStreamRpcSchema<Name extends string, Input, Output> = {
  name: Name;
  input: z.ZodType<Input>;
  output: z.ZodType<Output>;
  method: "unary-stream";
};

export type RpcSchema<Name extends string, Input, Output> =
  | UnaryRpcSchema<Name, Input, Output>
  | UnaryStreamRpcSchema<Name, Input, Output>;

export type RpcClientSchema<Name extends string, Input, Output> = {
  name: Name;
  input: Input;
  output: Output;
  method: "unary-stream" | "unary";
};

export type RpcDefinition = { [key: string]: RpcSchema<string, any, any> };

export type RpcClientDefinition = {
  [key: string]: RpcClientSchema<string, any, any>;
};

export type InferRpcDefinition<T> = T extends RpcDefinition
  ? { [key in keyof T]: InferRpcSchema<T[key]> }
  : never;

type InferRpcSchema<T> =
  T extends UnaryStreamRpcSchema<infer N, infer I, infer O>
    ? {
        name: N;
        input: I extends Serializable ? I : "Error: unserializable data";
        output: O extends Serializable ? O : "Error: unserializable data";
        method: "unary-stream";
      }
    : T extends UnaryRpcSchema<infer N, infer I, infer O>
      ? {
          name: N;
          input: I extends Serializable ? I : "Error: unserializable data";
          output: O extends Serializable ? O : "Error: unserializable data";
          method: "unary";
        }
      : never;
